import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import useSWR from "swr";
import { z } from "zod";

const BonusGroupTicketAttachmentSchema = z.object({
  _id: z.string(),
});
type BonusGroupTicketAttachment = z.infer<
  typeof BonusGroupTicketAttachmentSchema
>;

const BonusGroupSchema = z.object({
  groupName: z.string(),
  bonusIds: z.array(z.string()),
  _id: z.string(),
});
type BonusGroup = z.infer<typeof BonusGroupSchema>;

const BonusMediaSchema = z.object({
  _id: z.string(),
  bonusName: z.string(),
});
type BonusMedia = z.infer<typeof BonusMediaSchema>;

const BonusGroupWithMediaSchema = z.object({
  group: BonusGroupSchema,
  mediaList: z.array(BonusMediaSchema),
});
type BonusGroupWithMedia = z.infer<typeof BonusGroupWithMediaSchema>;

/**
 * Firestoreからボーナスグループチケット紐付けを取得する
 * バリデーションエラーが発生したドキュメントはスキップして、正常なデータのみを返す
 */
const fetchBonusGroupsTicketAttachment = async (
  eventId: string,
  ticketId: string
): Promise<BonusGroupTicketAttachment[]> => {
  const collectionPath = `tickets/${eventId}/tickets/${ticketId}/bonusGroups`;
  const snapshot = await firebase.firestore().collection(collectionPath).get();

  const results: BonusGroupTicketAttachment[] = [];

  for (const doc of snapshot.docs) {
    try {
      const data = { ...doc.data(), _id: doc.id };
      const validated = BonusGroupTicketAttachmentSchema.parse(data);
      results.push(validated);
    } catch (error) {
      console.error(
        `Zod validation failed for BonusGroupTicketAttachment (${doc.id}):`,
        error
      );
    }
  }

  return results;
};

/**
 * バリデーションエラーが発生した場合はundefinedを返す
 */
const fetchBonusGroup = async (
  bonusGroupId: string
): Promise<BonusGroup | undefined> => {
  const docPath = `bonusGroups/${bonusGroupId}`;
  const snapshot = await firebase.firestore().doc(docPath).get();

  if (!snapshot.exists) {
    console.warn(`BonusGroup not found: ${bonusGroupId}`);
    return undefined;
  }

  try {
    const data = { ...snapshot.data(), _id: snapshot.id };
    return BonusGroupSchema.parse(data);
  } catch (error) {
    console.error(
      `Zod validation failed for BonusGroup (${bonusGroupId}):`,
      error
    );
    return undefined;
  }
};

/**
 * バリデーションエラーが発生した場合はundefinedを返す
 */
const fetchBonusMedia = async (
  bonusMediaId: string
): Promise<BonusMedia | undefined> => {
  const docPath = `bonusMedia/${bonusMediaId}`;
  const snapshot = await firebase.firestore().doc(docPath).get();

  if (!snapshot.exists) {
    console.warn(`BonusMedia not found: ${bonusMediaId}`);
    return undefined;
  }

  try {
    const data = { ...snapshot.data(), _id: snapshot.id };
    return BonusMediaSchema.parse(data);
  } catch (error) {
    console.error(
      `Zod validation failed for BonusMedia (${bonusMediaId}):`,
      error
    );
    return undefined;
  }
};

type UseBonusGroupsParams = {
  eventId: string;
  ticketId: string;
  enabled: boolean;
};

type UseBonusGroupsReturn = {
  bonusGroupsWithMedia: BonusGroupWithMedia[];
  isLoading: boolean;
  error: Error | undefined;
};

/**
 * ボーナスグループデータを取得するfetcher関数
 */
const fetchBonusGroupsWithMedia = async (
  eventId: string,
  ticketId: string
): Promise<BonusGroupWithMedia[]> => {
  const groups = await fetchBonusGroupsTicketAttachment(eventId, ticketId);

  const groupDetailsPromises = groups.map(async (group) =>
    fetchBonusGroup(group._id)
  );

  const groupDetailsResults = await Promise.all(groupDetailsPromises);
  const groupDetails = groupDetailsResults.flatMap((group) => {
    return group !== undefined ? [group] : [];
  });

  const allBonusIds = groupDetails.flatMap((group) => group.bonusIds);
  const uniqueBonusIds = Array.from(new Set(allBonusIds));

  const bonusMediaPromises = uniqueBonusIds.map(async (bonusId) =>
    fetchBonusMedia(bonusId)
  );

  const bonusMediaResults = await Promise.all(bonusMediaPromises);
  const bonusMedia = bonusMediaResults.flatMap((media) => {
    return media !== undefined ? [media] : [];
  });

  const bonusMediaMap = new Map(bonusMedia.map((media) => [media._id, media]));

  const groupsWithMedia: BonusGroupWithMedia[] = groupDetails.map((group) => ({
    group,
    mediaList: group.bonusIds
      .map((bonusId) => bonusMediaMap.get(bonusId))
      .filter((media): media is BonusMedia => media !== undefined),
  }));

  return groupsWithMedia;
};

/**
 * チケットに紐づくボーナスグループとメディアを取得するカスタムフック
 */
export const useBonusGroups = ({
  eventId,
  ticketId,
  enabled,
}: UseBonusGroupsParams): UseBonusGroupsReturn => {
  const swrKey = enabled ? ["bonusGroups", eventId, ticketId] : null;

  const { data, error, isLoading } = useSWR<
    BonusGroupWithMedia[],
    Error | undefined
  >(swrKey, async () => fetchBonusGroupsWithMedia(eventId, ticketId), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return {
    bonusGroupsWithMedia: data ?? [],
    isLoading,
    error,
  };
};
